import { useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";

export default function SetInitialPassword() {
  const { refresh, logout } = useAuth();
  const [pwd, setPwd] = useState({ next: "", confirm: "" });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (pwd.next.length < 6) { setError("Password must be at least 6 characters"); return; }
    if (pwd.next !== pwd.confirm) { setError("Passwords do not match"); return; }
    setSubmitting(true);
    try {
      await api.post("/auth/set-initial-password", { new_password: pwd.next });
      toast.success("Password set — welcome to the studio");
      await refresh();
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || "Failed to set password");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-canvas p-8" data-testid="set-initial-password-page">
      <form onSubmit={submit} className="w-full max-w-sm space-y-6" data-testid="set-password-form">
        <div>
          <div className="ui-label">Secure your account</div>
          <h2 className="font-heading text-3xl font-bold mt-2">Set your password</h2>
          <p className="text-sm text-fg2 mt-2">
            You're signing in for the first time. Choose a password to continue.
          </p>
        </div>

        <div className="space-y-4">
          <div>
            <Label htmlFor="np" className="ui-label">New password</Label>
            <Input id="np" type="password" value={pwd.next} onChange={(e) => setPwd({ ...pwd, next: e.target.value })}
              className="mt-2 bg-surface border-line focus-visible:ring-[#FF3B30]/50" data-testid="set-new-password-input" required />
          </div>
          <div>
            <Label htmlFor="cp" className="ui-label">Confirm password</Label>
            <Input id="cp" type="password" value={pwd.confirm} onChange={(e) => setPwd({ ...pwd, confirm: e.target.value })}
              className="mt-2 bg-surface border-line focus-visible:ring-[#FF3B30]/50" data-testid="set-confirm-password-input" required />
          </div>
        </div>

        {error && (
          <div data-testid="set-password-error" className="text-sm text-[#FF3B30] bg-[#FF3B30]/10 border border-[#FF3B30]/20 rounded-md px-3 py-2">
            {error}
          </div>
        )}

        <Button type="submit" disabled={submitting} data-testid="set-password-submit"
          className="w-full bg-[#FF3B30] hover:bg-[#D63026] text-white font-medium h-11">
          {submitting ? "Saving..." : "Set password & continue"}
        </Button>
        <button type="button" onClick={logout} data-testid="set-password-signout"
          className="w-full text-sm text-fg3 hover:text-fg2 transition">
          Sign out
        </button>
      </form>
    </div>
  );
}
